import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import clsx from "clsx";

interface PollEditorProps {
  programItemId: string;
  onClose: () => void;
}

export default function PollEditorModal({
  programItemId,
  onClose,
}: PollEditorProps) {
  const [groups, setGroups] = useState<any[]>([]);
  const [editedGroupTitles, setEditedGroupTitles] = useState<
    Record<string, string>
  >({});
  const [editedGroupRequired, setEditedGroupRequired] = useState<
    Record<string, boolean>
  >({});
  const [options, setOptions] = useState<any[]>([]);
  const [savingGroupId, setSavingGroupId] = useState<string | null>(null);
  const [newOptionTexts, setNewOptionTexts] = useState<
    Record<string, { label: string; price: string; note: string }>
  >({});
  const [itemMeta, setItemMeta] = useState<{
    poll_answer_deadline: string | null;
    poll_target_info: string | null;
    poll_allow_abstain: boolean | null;
  }>({
    poll_answer_deadline: null,
    poll_target_info: null,
    poll_allow_abstain: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const { data: item } = await supabase
        .from("trip_program_items")
        .select("poll_answer_deadline, poll_target_info, poll_allow_abstain")
        .eq("id", programItemId)
        .single();

      const { data: g } = await supabase
        .from("trip_program_poll_option_groups")
        .select("*")
        .eq("program_item_id", programItemId);

      const { data: o } = await supabase
        .from("trip_program_poll_options")
        .select("*")
        .in("option_group_id", g?.map((gr) => gr.id) ?? []);

      setItemMeta(
        item ?? {
          poll_answer_deadline: null,
          poll_target_info: "",
          poll_allow_abstain: false,
        }
      );
      setGroups(g || []);
      setOptions(o || []);
      setLoading(false);
    };
    fetchData();
  }, [programItemId]);

  const updateGroup = async (id: string, changes: any) => {
    setSavingGroupId(id);
    const { error } = await supabase
      .from("trip_program_poll_option_groups")
      .update(changes)
      .eq("id", id);

    if (error) {
      console.error("Fehler beim Speichern der Gruppe:", error);
    } else {
      setGroups((prev) =>
        prev.map((g) => (g.id === id ? { ...g, ...changes } : g))
      );
    }
    setSavingGroupId(null);
  };

  const deleteGroup = async (id: string) => {
    await supabase
      .from("trip_program_poll_option_groups")
      .delete()
      .eq("id", id);
    setGroups((prev) => prev.filter((g) => g.id !== id));
    setOptions((prev) => prev.filter((o) => o.option_group_id !== id));
  };

  const updateItemMeta = async (field: string, value: any) => {
    setItemMeta((prev) => ({ ...prev, [field]: value }));
    await supabase
      .from("trip_program_items")
      .update({ [field]: value })
      .eq("id", programItemId);
  };

  const handleAddOption = async (groupId: string) => {
    const { label, price, note } = newOptionTexts[groupId] || {
      label: "",
      price: "",
      note: "",
    };
    if (!label.trim()) return;

    const { data, error } = await supabase
      .from("trip_program_poll_options")
      .insert({
        option_group_id: groupId,
        label,
        price: price ? parseFloat(price) : undefined,
        note: note || null,
      })
      .select();

    if (data?.[0]) {
      setOptions((prev) => [...prev, data[0]]);
      setNewOptionTexts((prev) => ({
        ...prev,
        [groupId]: { label: "", price: "", note: "" },
      }));
    }
  };

  const deleteOption = async (optionId: string) => {
    await supabase
      .from("trip_program_poll_options")
      .delete()
      .eq("id", optionId);
    setOptions((prev) => prev.filter((o) => o.id !== optionId));
  };

  const addGroup = async () => {
    const { data } = await supabase
      .from("trip_program_poll_option_groups")
      .insert({ program_item_id: programItemId, title: "Neue Auswahlgruppe" })
      .select();

    if (data?.[0]) {
      setGroups((prev) => [...prev, data[0]]);
    }
  };

  if (loading) return <p className="p-6 text-center">Lade Umfrage...</p>;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-[#161b27] border border-[#2a3347] w-full max-w-2xl p-6 rounded-2xl relative overflow-y-auto max-h-[95vh]">
        <h2 className="text-sm font-semibold text-slate-100 mb-5">
          Umfrage konfigurieren
        </h2>

        <div className="mb-3">
          <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Zielgruppe (Info)</label>
          <input
            type="text"
            value={itemMeta.poll_target_info || ""}
            onChange={(e) => updateItemMeta("poll_target_info", e.target.value)}
            className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50"
          />
        </div>

        <div className="mb-3">
          <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Antwort-Deadline</label>
          <input
            type="datetime-local"
            value={itemMeta.poll_answer_deadline?.slice(0, 16) ?? ""}
            onChange={(e) =>
              updateItemMeta("poll_answer_deadline", e.target.value)
            }
            className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50"
          />
        </div>

        <div className="mb-5">
          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <input
              type="checkbox"
              checked={!!itemMeta.poll_allow_abstain}
              onChange={(e) =>
                updateItemMeta("poll_allow_abstain", e.target.checked)
              }
              className="accent-sky-500"
            />
            "Ich bin nicht dabei" erlauben
          </label>
        </div>

        {groups.map((group) => {
          const groupOptions = options.filter(
            (o) => o.option_group_id === group.id
          );
          const newOpt = newOptionTexts[group.id] || {
            label: "",
            price: "",
            note: "",
          };

          return (
            <div key={group.id} className="bg-[#1e2639] border border-[#2a3347] rounded-xl p-4 mb-4">
              <div className="flex justify-between items-center mb-3">
                <input
                  type="text"
                  value={editedGroupTitles[group.id] ?? group.title ?? ""}
                  onChange={(e) =>
                    setEditedGroupTitles((prev) => ({
                      ...prev,
                      [group.id]: e.target.value,
                    }))
                  }
                  onBlur={() => {
                    const newTitle = editedGroupTitles[group.id];
                    if (newTitle !== undefined && newTitle !== group.title) {
                      updateGroup(group.id, { title: newTitle });
                    }
                  }}
                  className="text-sm font-semibold text-slate-100 w-full bg-transparent border-b border-[#2a3347] focus:outline-none focus:border-sky-500/50 pb-1"
                />
                <button
                  onClick={() => deleteGroup(group.id)}
                  className="text-red-400 text-xs hover:text-red-300 ml-4 whitespace-nowrap transition-colors"
                >
                  Löschen
                </button>
              </div>

              <div className="mb-3">
                <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Max. Auswahl</label>
                <input
                  type="number"
                  value={group.max_selections ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateGroup(group.id, {
                      max_selections: val === "" ? null : parseInt(val),
                    });
                  }}
                  className="w-full bg-[#161b27] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50"
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editedGroupRequired[group.id] ?? group.is_required}
                    onChange={(e) => {
                      const newVal = e.target.checked;
                      setEditedGroupRequired((prev) => ({
                        ...prev,
                        [group.id]: newVal,
                      }));
                      updateGroup(group.id, { is_required: newVal });
                    }}
                    className="accent-sky-500"
                  />
                  Pflicht
                </label>
              </div>

              <div className="mb-4 space-y-2">
                <label className="block text-[10px] uppercase tracking-widest text-slate-600">Neue Option</label>
                <input
                  type="text"
                  placeholder="Bezeichnung"
                  value={newOpt.label}
                  onChange={(e) =>
                    setNewOptionTexts((prev) => ({
                      ...prev,
                      [group.id]: { ...newOpt, label: e.target.value },
                    }))
                  }
                  className="w-full bg-[#161b27] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
                />
                <input
                  type="number"
                  step="0.05"
                  placeholder="Preis (optional)"
                  value={newOpt.price}
                  onChange={(e) =>
                    setNewOptionTexts((prev) => ({
                      ...prev,
                      [group.id]: { ...newOpt, price: e.target.value },
                    }))
                  }
                  className="w-full bg-[#161b27] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
                />
                <input
                  type="text"
                  placeholder="Bemerkung (optional)"
                  value={newOpt.note}
                  onChange={(e) =>
                    setNewOptionTexts((prev) => ({
                      ...prev,
                      [group.id]: { ...newOpt, note: e.target.value },
                    }))
                  }
                  className="w-full bg-[#161b27] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
                />
                <button
                  onClick={() => handleAddOption(group.id)}
                  className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors"
                >
                  Hinzufügen
                </button>
              </div>

              <div className="space-y-1.5">
                {groupOptions.map((opt) => (
                  <div
                    key={opt.id}
                    className="flex justify-between items-start bg-[#161b27] border border-[#2a3347] px-3 py-2.5 rounded-xl"
                  >
                    <div>
                      <div className="text-sm text-slate-300">{opt.label}</div>
                      {opt.note && (
                        <div className="text-xs text-slate-500 italic">
                          {opt.note}
                        </div>
                      )}
                      {opt.price && (
                        <div className="text-xs text-slate-500">
                          {opt.price.toFixed(2)} CHF
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => deleteOption(opt.id)}
                      className="text-red-400 hover:text-red-300 text-xs transition-colors"
                    >
                      Entfernen
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <button
          onClick={addGroup}
          className="w-full mt-2 text-xs px-3 py-2 rounded-xl border border-[#2a3347] text-slate-400 hover:border-sky-500/30 hover:text-sky-400 transition-colors"
        >
          ➕ Neue Auswahlgruppe hinzufügen
        </button>

        <div className="flex justify-end mt-5">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors"
          >
            Speichern & Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
